import { ArrowUpRight, Bot, CheckCircle2, Clock3, Flame, Loader2, Rocket, Search, Shield, Sparkles, UserRound } from "lucide-react";
import { clsx } from "clsx";
import { DelegationTarget, Mission } from "../../lib/commandCore";
import type { InboxSuggestion } from "../../lib/tauri";
import { providerLabel } from "../../lib/ai-pwa";

interface Props {
  missions: Mission[];
  onOpenMission: (id: string) => void;
  /** Phase 4: gemma の委任先当たり付け（taskId→提案）。参考表示のみで並びを変えない */
  advice?: Record<string, InboxSuggestion>;
  adviceLoading?: boolean;
  onRefreshAdvice?: () => void;
}

const ownerMeta: Record<DelegationTarget, { label: string; icon: typeof Bot; tone: string }> = {
  taiga: { label: "Taiga", icon: UserRound, tone: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-400/15 dark:text-emerald-100 dark:border-emerald-400/30" },
  codex: { label: providerLabel("codex"), icon: Bot, tone: "bg-indigo-50 text-indigo-800 border-indigo-200 dark:bg-indigo-400/15 dark:text-indigo-100 dark:border-indigo-400/30" },
  claude: { label: providerLabel("claude"), icon: Shield, tone: "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-400/15 dark:text-amber-100 dark:border-amber-400/30" },
  grok: { label: "Grok", icon: Search, tone: "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-400/15 dark:text-sky-100 dark:border-sky-400/30" },
  hermes: { label: "Hermes", icon: CheckCircle2, tone: "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-400/15 dark:text-rose-100 dark:border-rose-400/30" },
};

export function PrimaryMissions({ missions, onOpenMission, advice, adviceLoading, onRefreshAdvice }: Props) {
  const fallback = missions[0]?.isFallback ? missions[0] : null;

  if (fallback) {
    return (
      <section className="command-card p-4 shadow-md dark:shadow-[0_18px_60px_-42px_rgba(129,140,248,0.9)]">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-brand-500 text-white dark:bg-indigo-400 dark:text-stone-950">
              <Rocket className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-brand-700 dark:text-indigo-200">ミッション起動</p>
              <h2 className="mt-1 text-2xl font-bold">今日の勝ち筋を1件だけ決める</h2>
              <p className="command-muted mt-2 max-w-2xl text-sm">
                空のTodo一覧ではなく、まず作戦室を起動する。今日の最重要を1件登録するか、コマンドシートでミッション案を作る。
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenMission(fallback.id)}
            className="rounded-md border border-brand-200 bg-brand-50 px-4 py-3 text-sm font-semibold text-brand-700 hover:bg-brand-100 dark:border-indigo-300/30 dark:bg-indigo-400/15 dark:text-indigo-100 dark:hover:bg-indigo-400/25"
          >
            GO B / ミッション案
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between text-text dark:text-white">
        <div>
          <h2 className="text-2xl font-bold">今日外さない3件</h2>
        </div>
        <div className="flex items-center gap-2">
          {onRefreshAdvice && (
            <button
              type="button"
              disabled={adviceLoading}
              onClick={onRefreshAdvice}
              title="gemma に委任先の当たり付けを聞く（参考表示のみ。並びは変わりません）"
              className="command-soft-control inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold disabled:opacity-50"
            >
              {adviceLoading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Sparkles className="h-3 w-3" />
              )}
              AI参考
            </button>
          )}
          <Flame className="h-5 w-5 text-amber-300" />
        </div>
      </div>
      <div className="grid gap-3 xl:grid-cols-3">
        {missions.map((mission, index) => {
          const owner = ownerMeta[mission.owner];
          const OwnerIcon = owner.icon;
          const adviceFor = advice?.[mission.id];
          const adviceLabel = adviceFor
            ? ownerMeta[adviceFor.owner as DelegationTarget]?.label ?? adviceFor.owner
            : null;
          return (
            <button
              key={mission.id}
              type="button"
              onClick={() => onOpenMission(mission.id)}
              className="command-card-hover group p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-text text-sm font-black text-bg dark:bg-white dark:text-stone-950">
                    {index + 1}
                  </span>
                  <span className={clsx("inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-semibold", owner.tone)}>
                    <OwnerIcon className="h-3.5 w-3.5" />
                    {owner.label}
                  </span>
                  {adviceLabel && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-bg px-2 py-1 text-[10px] text-muted dark:border-white/15 dark:bg-white/5 dark:text-zinc-300">
                      AI参考: {adviceLabel} {Math.round((adviceFor?.confidence ?? 0) * 100)}%
                    </span>
                  )}
                </div>
                <ArrowUpRight className="h-4 w-4 text-dim transition group-hover:text-brand-600 dark:text-zinc-500 dark:group-hover:text-indigo-200" />
              </div>
              <h3 className="mt-4 line-clamp-2 min-h-[3.5rem] break-words text-xl font-bold">
                {mission.title}
              </h3>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="inline-flex items-center gap-1 rounded-full border border-border bg-bg px-2 py-1 text-xs text-muted dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200">
                  <Clock3 className="h-3 w-3" />
                  {mission.dueLabel}
                </span>
                <span className="rounded-full border border-brand-200 bg-brand-50 px-2 py-1 font-mono text-xs text-brand-700 dark:border-indigo-400/25 dark:bg-indigo-400/10 dark:text-indigo-100">
                  GO {mission.action}
                </span>
                {mission.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="rounded-full border border-border bg-bg px-2 py-1 text-xs text-muted dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-300">
                    {tag}
                  </span>
                ))}
              </div>
              <div className="mt-4 space-y-3 text-sm">
                <p className="command-muted line-clamp-2">
                  <span className="font-semibold text-text dark:text-zinc-100">理由: </span>
                  {mission.reason}
                </p>
                <div className="grid grid-cols-3 gap-1.5">
                  <div className="command-subcard px-2 py-1.5">
                    <p className="command-dim text-[10px]">出所</p>
                    <p className="truncate text-xs font-semibold">{mission.source}</p>
                  </div>
                  <div className="command-subcard px-2 py-1.5">
                    <p className="command-dim text-[10px]">確度</p>
                    <p className="truncate text-xs font-semibold">{mission.confidence}</p>
                  </div>
                  <div className="command-subcard px-2 py-1.5">
                    <p className="command-dim text-[10px]">鮮度</p>
                    <p className="truncate text-xs font-semibold">{mission.freshness}</p>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
